import { useQuery } from "@apollo/client/react";
import { type GQLBrandSelectionQuery, type GQLBrandSelectionQueryVariables } from "@src/graphql.generated";
import { useAttributeConfig } from "@src/hooks/useAttributeConfig";
import { useGlobalState } from "@src/hooks/useGlobalState";
import { brandSelectionQuery } from "@src/pages/landingPage/components/brandSelection/BrandSelection.gql";
import { useEffect, useState } from "react";

export type SupportedBrandsArray = GQLBrandSelectionQuery["brands"]["nodes"];

/**
 * Brands that are still sold. Everything else the API returns is deprecated in Bajaj
 * mobility and must not be offered as a choice (work item #499836).
 */
const allowedBrandShortNames: readonly string[] = ["ktm", "gg", "hqv", "wp"];

const isAllowedBrand = (brand: SupportedBrandsArray[number]) => allowedBrandShortNames.includes(brand.shortName.toLowerCase());

/**
 * Custom hook to fetch and manage supported brands.
 *
 * Deprecated brands are filtered out, but never at the cost of breaking an existing embed:
 * a host page that configured only deprecated brands keeps them, because replacing its
 * catalogue with brands it never asked for would be worse than showing a deprecated one.
 *
 * @returns {Object} An object containing:
 * - supportedBrandIds: An array of supported brand IDs.
 * - supportedBrands: An array of supported brand objects.
 * - loading: A boolean indicating if the data is currently being loaded.
 * - error: The error if the query failed.
 */
export const useSupportedBrands = () => {
    const { brands: attributeBrands } = useAttributeConfig();
    const { setBrandId, setBreadcrumbs, setSupportedBrandIds: setGlobalSupportedBrandIds } = useGlobalState();

    const [supportedIds, setSupportedIds] = useState<string[]>([]);
    const [supportedBrands, setSupportedBrands] = useState<SupportedBrandsArray>([]);

    const { data, loading, error } = useQuery<GQLBrandSelectionQuery, GQLBrandSelectionQueryVariables>(brandSelectionQuery);

    // runs once per new result, like the onCompleted callback Apollo Client 4 removed
    useEffect(() => {
        if (!data) return;

        const apiBrands = data.brands.nodes;

        // brands the host page asked for, in the order it configured them, ignoring
        // codes this environment does not know and codes listed more than once
        const requestedBrands: SupportedBrandsArray = [];
        attributeBrands?.forEach((brand) => {
            const brandData = apiBrands.find((apiBrand) => apiBrand.shortName.toLowerCase() === brand.trim().toLowerCase());
            if (brandData && !requestedBrands.includes(brandData)) {
                requestedBrands.push(brandData);
            }
        });

        const allowedRequestedBrands = requestedBrands.filter(isAllowedBrand);
        const allowedBrands = apiBrands.filter(isAllowedBrand);

        let brands: SupportedBrandsArray;
        if (allowedRequestedBrands.length > 0) {
            // the host asked for at least one brand that is still sold, so offer only those
            brands = allowedRequestedBrands;
        } else if (requestedBrands.length > 0) {
            // the host asked only for deprecated brands, so leave its embed as it was
            brands = requestedBrands;
        } else if (allowedBrands.length > 0) {
            // nothing usable configured, so offer everything that is still sold
            brands = allowedBrands;
        } else {
            // this environment has none of the allowed brands, and an empty picker is worse
            brands = apiBrands;
        }

        const ids = brands.map((brand) => brand.id);

        // keyed on what the host configured, not on what survives the allowlist, so an
        // embed that used to show a brand picker still shows one
        if (requestedBrands.length === 1) {
            // only one brand supported, directly set brand id and first breadcrumb of visual selector
            setBreadcrumbs([{ categoryId: requestedBrands[0].id, description: requestedBrands[0].name }]);
            setBrandId(requestedBrands[0].id);
        }

        setSupportedIds(ids);
        setSupportedBrands(brands);
        setGlobalSupportedBrandIds(ids);
        // only react to new data: the setters from global state aren't guaranteed to be stable and onCompleted didn't depend on them either
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    return {
        supportedBrandIds: supportedIds,
        supportedBrands,
        loading,
        error,
    };
};
